import { apiService } from '@/api/apiConfig';
import {
  WorkActivity,
  WorkActivityListResponse,
  WorkActivityPerformanceResponse,
  WorkActivityResponse
} from '@/services/types/work-activity.types';

const handleWorkActivityError = (error: any): Error => {
  if (error instanceof Error && error.message) {
    return error;
  }
  if (error.response?.data?.message) {
    return new Error(error.response.data.message);
  }
  return new Error('Work activity operation failed');
};

const normalizeWorkActivityList = (response: unknown): WorkActivity[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== 'object') {
    return [];
  }

  const payload = response as Record<string, unknown>;
  const data = payload.data;

  if (Array.isArray(data)) {
    return data as WorkActivity[];
  }

  if (data && typeof data === 'object') {
    const nestedData = (data as Record<string, unknown>).data;
    if (Array.isArray(nestedData)) {
      return nestedData as WorkActivity[];
    }
  }

  return [];
};

export const workActivityService = {
  createWorkActivity: async (budgetId: string, formData: FormData): Promise<WorkActivity> => {
    try {
      const response = await apiService.request<WorkActivityResponse>({
        method: 'POST',
        url: `/budget/${budgetId}/work-activity`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw handleWorkActivityError(error);
    }
  },

  getWorkActivities: async (
    budgetId: string,
    params?: {
      status?: string;
      type?: 'mine' | 'to_review' | 'all';
      accounting_center_id?: string;
      search?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<WorkActivityListResponse> => {
    try {
      const response = await apiService.request<unknown>({
        method: 'GET',
        url: `/budget/${budgetId}/work-activity`,
        params
      });

      const items = normalizeWorkActivityList(response);

      const metaSource: Partial<WorkActivityListResponse> =
        response && typeof response === 'object' && 'totalCount' in response
          ? (response as WorkActivityListResponse)
          : response &&
              typeof response === 'object' &&
              'data' in response &&
              typeof (response as { data: unknown }).data === 'object' &&
              (response as { data: unknown }).data !== null &&
              !Array.isArray((response as { data: unknown }).data)
            ? (response as { data: WorkActivityListResponse }).data
            : {};

      return {
        data: items,
        totalCount: metaSource.totalCount ?? items.length,
        totalPages: metaSource.totalPages ?? 1,
        currentPage: metaSource.currentPage ?? 1
      };
    } catch (error) {
      throw handleWorkActivityError(error);
    }
  },

  getWorkActivity: async (budgetId: string, workActivityId: string): Promise<WorkActivity> => {
    try {
      const response = await apiService.request<WorkActivityResponse | { data: WorkActivity }>({
        method: 'GET',
        url: `/budget/${budgetId}/work-activity/${workActivityId}`
      });

      const workActivity =
        response && typeof response === 'object' && 'data' in response && response.data && '_id' in response.data
          ? response.data
          : (response as WorkActivityResponse).data;

      return workActivity;
    } catch (error) {
      throw handleWorkActivityError(error);
    }
  },

  approveWorkActivity: async (budgetId: string, workActivityId: string): Promise<WorkActivity> => {
    try {
      const response = await apiService.request<WorkActivityResponse | { data: WorkActivity }>({
        method: 'POST',
        url: `/budget/${budgetId}/work-activity/${workActivityId}/approve`
      });

      return response && typeof response === 'object' && 'data' in response && response.data && '_id' in response.data
        ? response.data
        : (response as WorkActivityResponse).data;
    } catch (error) {
      throw handleWorkActivityError(error);
    }
  },

  rejectWorkActivity: async (
    budgetId: string,
    workActivityId: string,
    rejectionNote: string
  ): Promise<WorkActivity> => {
    try {
      const response = await apiService.request<WorkActivityResponse | { data: WorkActivity }>({
        method: 'POST',
        url: `/budget/${budgetId}/work-activity/${workActivityId}/reject`,
        data: { rejection_note: rejectionNote }
      });

      return response && typeof response === 'object' && 'data' in response && response.data && '_id' in response.data
        ? response.data
        : (response as WorkActivityResponse).data;
    } catch (error) {
      throw handleWorkActivityError(error);
    }
  },

  resubmitWorkActivity: async (budgetId: string, workActivityId: string, formData: FormData): Promise<WorkActivity> => {
    try {
      const response = await apiService.request<WorkActivityResponse | { data: WorkActivity }>({
        method: 'POST',
        url: `/budget/${budgetId}/work-activity/${workActivityId}/resubmit`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response && typeof response === 'object' && 'data' in response && response.data && '_id' in response.data
        ? response.data
        : (response as WorkActivityResponse).data;
    } catch (error) {
      throw handleWorkActivityError(error);
    }
  },

  updateWorkActivity: async (budgetId: string, workActivityId: string, formData: FormData): Promise<WorkActivity> => {
    try {
      const response = await apiService.request<WorkActivityResponse | { data: WorkActivity }>({
        method: 'PATCH',
        url: `/budget/${budgetId}/work-activity/${workActivityId}`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response && typeof response === 'object' && 'data' in response && response.data && '_id' in response.data
        ? response.data
        : (response as WorkActivityResponse).data;
    } catch (error) {
      throw handleWorkActivityError(error);
    }
  },

  deleteWorkActivity: async (budgetId: string, workActivityId: string): Promise<void> => {
    try {
      await apiService.request({
        method: 'DELETE',
        url: `/budget/${budgetId}/work-activity/${workActivityId}`
      });
    } catch (error) {
      throw handleWorkActivityError(error);
    }
  },

  getWorkActivityPerformance: async (
    budgetId: string,
    params?: { year?: number }
  ): Promise<WorkActivityPerformanceResponse> => {
    try {
      const response = await apiService.request<
        WorkActivityPerformanceResponse | { data: WorkActivityPerformanceResponse }
      >({
        method: 'GET',
        url: `/budget/${budgetId}/work-activity/performance`,
        params
      });

      if (
        response &&
        typeof response === 'object' &&
        'data' in response &&
        response.data &&
        'series' in response.data
      ) {
        return response.data;
      }

      return response as WorkActivityPerformanceResponse;
    } catch (error) {
      throw handleWorkActivityError(error);
    }
  }
};
